/**
 * Assessment and progression type definitions from PRD Sections 5 and 8
 */

// Note: LifeFunction and Classification types from game.types are available but not directly used here

// Mastery Levels (PRD 5.2)
export enum MasteryLevel {
  NOT_INTRODUCED = 0,
  INTRODUCED = 1,
  PRACTICING = 2,
  DEVELOPING = 3,
  PROFICIENT = 4,
  MASTERED = 5,
}

// Level Types
export type LevelType = 'discovery' | 'classification' | 'simulation' | 'builder' | 'challenge' | 'exploration';

// Level Objective Types
export interface LevelObjective {
  type: string;
  count?: number;
  accuracy?: number;
  threshold?: number;
  functions?: string[];
  organisms?: number;
  expect?: string;
  quiz?: boolean;
  essay?: boolean;
  patterns?: string[];
  seconds?: number;
  // Builder-related objectives
  generations?: number;
  observe?: string;
  components?: string[];
  pass?: boolean;
  cellCount?: number;
  tissueCount?: number;
}

// Level Definition (PRD 5.1)
export interface Level {
  id: string;
  name: string;
  type: LevelType;
  specimens?: string[];
  objectives: LevelObjective[];
  tutorialElements?: string[];
  focus?: string[];
  mechanics?: string[];
  narrative?: string;
  challenge?: string;
  components?: string[];
  tissueTypes?: string[];
  organs?: string[];
  requirements?: string[];
}

// Unlock Requirements
export interface UnlockRequirement {
  type: 'chapter_complete' | 'level_complete' | 'score_threshold' | 'concept_mastery';
  targetId?: string;
  threshold?: number;
  conceptId?: string;
  masteryLevel?: MasteryLevel;
}

// Boss Challenge
export interface BossChallenge {
  name: string;
  description: string;
  specimens?: string[];
  challenges?: string[];
  difficulty?: 'easy' | 'medium' | 'hard';
}

// Chapter Definition (PRD 5.1)
export interface Chapter {
  id: string;
  title: string;
  learningObjectives: string[];
  levels: Level[];
  unlockRequirement?: UnlockRequirement;
  bossChallenge?: BossChallenge;
}

// Concept Definition (from Assignment.md)
export interface Concept {
  id: string;
  text: string;
  weight: 'Core' | 'Peripheral';
  standardId: string;
  assessmentCriteria?: string[];
}

// Assessment Types (PRD 8.1)
export type AssessmentType = 'implicit' | 'checkpoint' | 'reflection';
export type AssessmentTrigger = 'classification_attempt' | 'chapter_end' | 'after_boss_challenge' | 'level_complete';

export interface AssessmentData {
  accuracy?: boolean;
  timeToDecide?: boolean;
  evidenceUsed?: boolean;
  confidenceLevel?: boolean;
  multipleChoice?: boolean;
  dragAndDrop?: boolean;
  explanationPrompt?: boolean;
  openResponse?: boolean;
  selfAssessment?: boolean;
}

export interface FormativeAssessment {
  type: AssessmentType;
  trigger: AssessmentTrigger;
  conceptsTested: string[];
  dataCollected: AssessmentData;
}

// Implicit Assessment Indicators (PRD 5.2)
export interface ImplicitIndicators {
  correctClassifications: number;
  incorrectClassifications: number;
  evidenceGathered: number;
  simulationsCompleted: number;
  hintsRequested: number;
  timeOnTask: number;
}

// Explicit Assessment Record
export interface ExplicitAssessment {
  questionId: string;
  response: string;
  correct: boolean;
  timestamp: Date;
}

// Concept Assessment (PRD 5.2)
export interface ConceptAssessment {
  conceptId: string;
  conceptText: string;
  weight: 'Core' | 'Peripheral';
  assessmentType: 'implicit' | 'explicit';
  implicitIndicators: ImplicitIndicators;
  explicitAssessments: ExplicitAssessment[];
  currentMastery: MasteryLevel;
}

// Adaptive Difficulty (PRD 5.3)
export interface DifficultyModifiers {
  specimenDifficulty: number; // 0.4-1.0
  timeMultiplier: number; // 0.5-2.0
  hintsEnabled: boolean;
  hintCooldown: number; // seconds
  evidenceRequired: number; // 3 or 5
  distractorSimilarity: number; // 0.3-0.9
}

export interface PerformanceRecord {
  levelId: string;
  score: number;
  accuracy: number;
  timeSpent: number;
  hintsUsed: number;
  timestamp: Date;
}

// Score System (PRD 5.2)
export interface LevelScore {
  accuracy: number; // 0-100
  completeness: number; // 0-100
  efficiency: number; // 0-100
  discovery: number; // 0-100 bonus
  total: number;
  stars: number; // 0-3
}

// Teacher Dashboard Data (PRD 8.2)
export interface StudentProfile {
  id: string;
  name?: string;
  completedLevels: string[];
  conceptMastery: Map<string, MasteryLevel>;
  timeOnTask: number;
  strugglingAreas: string[];
  strengths: string[];
  lastActive: Date;
}

export interface ClassProgress {
  averageCompletion: number;
  conceptMasteryDistribution: Map<string, number[]>;
  commonMisconceptions: Misconception[];
}

export interface Misconception {
  conceptId: string;
  description: string;
  frequency: number;
  suggestedIntervention: string;
}

export interface StandardsReport {
  standardId: string;
  classAverage: number;
  evidenceItems: string[];
}

export interface TeacherDashboardData {
  classProgress: ClassProgress;
  studentProfiles: StudentProfile[];
  standardsReport: StandardsReport[];
}

// Analytics Events (PRD 10.2)
export type AnalyticsEventType =
  | 'level_start'
  | 'level_complete'
  | 'classification_attempt'
  | 'evidence_collected'
  | 'concept_introduced'
  | 'concept_assessed'
  | 'hint_requested'
  | 'tutorial_skipped'
  | 'difficulty_adjusted'
  | 'session_duration';

export interface AnalyticsEvent {
  eventType: AnalyticsEventType;
  timestamp: Date;
  sessionId: string;
  userId?: string;
  data: Record<string, unknown>;
}
